import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Clock, Check, CalendarPlus, Droplets, Zap, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { SelfWashBookingDialog } from "@/components/SelfWashBookingDialog";
import { loadPlanPrices } from "@/lib/plans";

const PLAN_KEYS = ["small", "medium", "large", "giant"];

export const SelfWashPricing = () => {
  const { t } = useLang();
  const s = t.selfwash;
  const [open, setOpen] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [prices, setPrices] = useState(null);

  useEffect(() => {
    loadPlanPrices().then(setPrices);
  }, []);

  const priceOf = (p, i) => prices?.[PLAN_KEYS[i]] ?? p.price;

  const book = (plan, key) => {
    setActivePlan({ ...plan, key });
    setOpen(true);
  };

  return (
    <section id="selfwash" className="relative py-24 sm:py-32 overflow-hidden" data-testid="selfwash">
      <div className="pointer-events-none absolute top-10 right-0 h-80 w-80 bg-brand-soft/50 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-72 w-72 bg-[#F3E7D9]/60 rounded-full blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        {/* BIG type badge — makes it unmistakably clear this is Self-Service booking */}
        <div className="flex justify-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-3 rounded-full bg-bamboo text-brand-cream px-6 py-3 shadow-[0_12px_40px_rgba(120,155,72,0.3)]"
            data-testid="selfwash-type-badge"
          >
            <span className="grid place-items-center h-9 w-9 rounded-full bg-brand-cream/20">
              <Droplets className="h-5 w-5" />
            </span>
            <span className="font-heading text-lg sm:text-xl font-bold tracking-wide uppercase">{s.type_label}</span>
          </motion.div>
        </div>

        <div className="max-w-2xl mx-auto text-center mt-6">
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-brand-ink tracking-tight">{s.title}</h2>
          <p className="mt-4 text-brand-ink/70 leading-relaxed text-lg">{s.subtitle}</p>
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-bamboo/25 px-4 py-1.5 text-sm font-bold text-bamboo-deep">
            <Zap className="h-3.5 w-3.5" /> {s.badge_instant}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand/15 px-4 py-1.5 text-sm font-bold text-brand">
            <ShieldCheck className="h-3.5 w-3.5" /> {s.badge_secure}
          </span>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {s.plans.map((p, i) => {
            const featured = i === 2;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -8 }}
                className={`relative rounded-[1.75rem] p-7 flex flex-col border transition-shadow duration-300 ${
                  featured
                    ? "bg-brand text-brand-cream border-brand shadow-[0_20px_50px_rgba(134,77,99,0.3)]"
                    : "bg-white text-brand-ink border-brand/10 shadow-[0_8px_30px_rgba(134,77,99,0.06)] hover:shadow-[0_16px_40px_rgba(134,77,99,0.14)]"
                }`}
                data-testid={`plan-${i}`}
              >
                {featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-ink text-brand-cream text-xs font-bold px-3 py-1">
                    {s.popular}
                  </span>
                )}
                <h3 className={`font-heading text-xl font-bold ${featured ? "text-brand-cream" : "text-brand-ink"}`}>{p.name}</h3>
                <div className={`mt-2 flex items-center gap-1.5 text-sm ${featured ? "text-brand-cream/80" : "text-brand-ink/60"}`}>
                  <Clock className="h-4 w-4" /> {p.min} {s.minutes}
                </div>
                <div className="mt-5 flex items-end gap-1">
                  <span className="font-heading text-5xl font-bold">€{priceOf(p, i)}</span>
                </div>
                <p className={`mt-4 text-sm leading-relaxed flex items-start gap-2 ${featured ? "text-brand-cream/80" : "text-brand-ink/60"}`}>
                  <Check className="h-4 w-4 mt-0.5 shrink-0" /> {s.includes}
                </p>
                <Button
                  data-testid={`plan-book-${i}`}
                  onClick={() => book({ ...p, price: priceOf(p, i) }, PLAN_KEYS[i])}
                  className={`mt-auto w-full rounded-full font-bold group no-a11y-underline ${
                    featured
                      ? "bg-brand-cream text-brand hover:bg-white"
                      : "bg-brand hover:bg-brand-hover text-brand-cream"
                  }`}
                >
                  <CalendarPlus className="h-4 w-4 mr-1.5" />
                  {s.choose}
                </Button>
              </motion.div>
            );
          })}
        </div>
      </div>

      <SelfWashBookingDialog open={open} onOpenChange={setOpen} plan={activePlan} />
    </section>
  );
};
